import React, { useState, createContext, useContext } from 'react';
// ============================================================================
// TYPES
// ============================================================================
export type UserStatus = 'active' | 'inactive' | 'suspended';
export type PartnerStatus = 'active' | 'inactive' | 'pending';
export type RewardStatus = 'Attivo' | 'Programmato' | 'Inattivo' | 'Scaduto';
export type ClaimStatus = 'Riscattato' | 'Utilizzato' | 'Annullato';
// Platform Analytics Types
export interface PlatformKPIs {
  totalUsers: number;
  engagementRate: number;
  revenue: number;
  retention: number;
}
export interface TimeSeriesData {
  date: string;
  value: number;
}
export interface SegmentData {
  id: string;
  name: string;
  users: number;
  engagement: number;
  revenue: number;
  trend: number;
}
export interface PlatformAnalytics {
  kpis: PlatformKPIs;
  usersSeries: TimeSeriesData[];
  revenueSeries: TimeSeriesData[];
  segments: SegmentData[];
}
// Impact Data Types
export interface ImpactKPIs {
  co2Saved: number; // kg
  plasticAvoided: number; // kg
  treesPlanted: number;
}
export interface ImpactTimeSeriesData {
  date: string;
  co2: number;
  plastic: number;
  trees: number;
}
export interface ImpactCategoryData {
  category: string;
  value: number;
}
export interface ImpactAction {
  id: string;
  name: string;
  category: string;
  completions: number;
  co2: number;
  lastUpdated: string;
  description: string;
}
export interface ImpactData {
  kpis: ImpactKPIs;
  impactSeries: ImpactTimeSeriesData[];
  categoryData: ImpactCategoryData[];
  topActions: ImpactAction[];
}
export interface User {
  id: string;
  avatar: string;
  nome: string;
  cognome: string;
  email: string;
  luogo: string;
  obiettivi: string;
  creditiDiamanteOttenuti: number;
  creditiDiamanteSpesi: number;
  creditiStellaOttenuti: number;
  creditiStellaSpesi: number;
  claimFatti: number;
  dataIscrizione: string;
  status: UserStatus;
}
export interface Partner {
  id: string;
  name: string;
  logo: string;
  website: string;
  email: string;
  description: string;
  categories: string[];
  locations: number;
  activeRewards: number;
  activeUsers: number;
  totalRedemptions: number;
  status: PartnerStatus;
}
export interface Reward {
  id: string;
  partnerId: string;
  code: string;
  name: string;
  partner: string;
  type: string;
  status: RewardStatus;
  cost: number;
  image: string;
  availability: {
    current: number;
    total: number;
    unlimited?: boolean;
  };
  redemptionsPeriod: number;
  redemptionsTotal: number;
  lastRedemption: string;
  location: string;
  startDate: string | null;
  endDate: string | null;
}
export interface Claim {
  id: string;
  date: string;
  reward: string;
  partner: string;
  partnerId: string;
  diamonds: number;
  userId: string;
  status: ClaimStatus;
  method: string;
  location: string;
  code: string;
  notes: string;
}
// ============================================================================
// INITIAL MOCK DATA
// ============================================================================
const initialUsers: User[] = [{
  id: 'USR-001',
  avatar: 'https://i.pravatar.cc/150?img=1',
  nome: 'Marco',
  cognome: 'Rossi',
  email: 'marco.rossi@email.it',
  luogo: 'Milano',
  obiettivi: 'Ridurre plastica',
  creditiDiamanteOttenuti: 850,
  creditiDiamanteSpesi: 450,
  creditiStellaOttenuti: 400,
  creditiStellaSpesi: 400,
  claimFatti: 12,
  dataIscrizione: '2025-01-15',
  status: 'active'
}, {
  id: 'USR-002',
  avatar: 'https://i.pravatar.cc/150?img=2',
  nome: 'Giulia',
  cognome: 'Bianchi',
  email: 'giulia.bianchi@email.it',
  luogo: 'Roma',
  obiettivi: 'Vita sostenibile',
  creditiDiamanteOttenuti: 1540,
  creditiDiamanteSpesi: 800,
  creditiStellaOttenuti: 800,
  creditiStellaSpesi: 400,
  claimFatti: 18,
  dataIscrizione: '2025-01-10',
  status: 'active'
}, {
  id: 'USR-003',
  avatar: 'https://i.pravatar.cc/150?img=3',
  nome: 'Alessandro',
  cognome: 'Ferrari',
  email: 'alessandro.ferrari@email.it',
  luogo: 'Torino',
  obiettivi: 'Zero waste',
  creditiDiamanteOttenuti: 490,
  creditiDiamanteSpesi: 200,
  creditiStellaOttenuti: 400,
  creditiStellaSpesi: 250,
  claimFatti: 7,
  dataIscrizione: '2025-02-01',
  status: 'active'
}, {
  id: 'USR-004',
  avatar: 'https://i.pravatar.cc/150?img=4',
  nome: 'Sofia',
  cognome: 'Romano',
  email: 'sofia.romano@email.it',
  luogo: 'Napoli',
  obiettivi: 'Cosmetici naturali',
  creditiDiamanteOttenuti: 1200,
  creditiDiamanteSpesi: 600,
  creditiStellaOttenuti: 650,
  creditiStellaSpesi: 300,
  claimFatti: 15,
  dataIscrizione: '2024-12-20',
  status: 'active'
}, {
  id: 'USR-005',
  avatar: 'https://i.pravatar.cc/150?img=5',
  nome: 'Lorenzo',
  cognome: 'Gallo',
  email: 'lorenzo.gallo@email.it',
  luogo: 'Bologna',
  obiettivi: 'Mobilità green',
  creditiDiamanteOttenuti: 680,
  creditiDiamanteSpesi: 350,
  creditiStellaOttenuti: 500,
  creditiStellaSpesi: 200,
  claimFatti: 9,
  dataIscrizione: '2025-01-05',
  status: 'active'
}, {
  id: 'USR-006',
  avatar: 'https://i.pravatar.cc/150?img=6',
  nome: 'Chiara',
  cognome: 'Conti',
  email: 'chiara.conti@email.it',
  luogo: 'Firenze',
  obiettivi: 'Alimentazione bio',
  creditiDiamanteOttenuti: 2100,
  creditiDiamanteSpesi: 1200,
  creditiStellaOttenuti: 1100,
  creditiStellaSpesi: 600,
  claimFatti: 24,
  dataIscrizione: '2024-11-15',
  status: 'active'
}, {
  id: 'USR-007',
  avatar: 'https://i.pravatar.cc/150?img=7',
  nome: 'Matteo',
  cognome: 'Ricci',
  email: 'matteo.ricci@email.it',
  luogo: 'Venezia',
  obiettivi: 'Energia rinnovabile',
  creditiDiamanteOttenuti: 320,
  creditiDiamanteSpesi: 150,
  creditiStellaOttenuti: 280,
  creditiStellaSpesi: 100,
  claimFatti: 5,
  dataIscrizione: '2025-02-10',
  status: 'active'
}, {
  id: 'USR-008',
  avatar: 'https://i.pravatar.cc/150?img=8',
  nome: 'Francesca',
  cognome: 'Marino',
  email: 'francesca.marino@email.it',
  luogo: 'Palermo',
  obiettivi: 'Ridurre sprechi',
  creditiDiamanteOttenuti: 950,
  creditiDiamanteSpesi: 500,
  creditiStellaOttenuti: 600,
  creditiStellaSpesi: 350,
  claimFatti: 11,
  dataIscrizione: '2024-12-28',
  status: 'active'
}, {
  id: 'USR-009',
  avatar: 'https://i.pravatar.cc/150?img=9',
  nome: 'Davide',
  cognome: 'Greco',
  email: 'davide.greco@email.it',
  luogo: 'Genova',
  obiettivi: 'Vita green',
  creditiDiamanteOttenuti: 1450,
  creditiDiamanteSpesi: 900,
  creditiStellaOttenuti: 850,
  creditiStellaSpesi: 500,
  claimFatti: 19,
  dataIscrizione: '2024-11-30',
  status: 'active'
}, {
  id: 'USR-010',
  avatar: 'https://i.pravatar.cc/150?img=10',
  nome: 'Elena',
  cognome: 'Bruno',
  email: 'elena.bruno@email.it',
  luogo: 'Verona',
  obiettivi: 'Riciclo attivo',
  creditiDiamanteOttenuti: 580,
  creditiDiamanteSpesi: 280,
  creditiStellaOttenuti: 420,
  creditiStellaSpesi: 180,
  claimFatti: 8,
  dataIscrizione: '2025-01-20',
  status: 'active'
}];
const initialPartners: Partner[] = [{
  id: 'PTR-001',
  name: 'La Saponaria',
  logo: 'https://blog.lasaponaria.it/hubfs/LaSaponaria-5.jpg',
  website: 'https://www.lasaponaria.it',
  email: 'info@lasaponaria.it',
  description: 'Cosmetici naturali e biologici certificati, prodotti in Italia con ingredienti sostenibili.',
  categories: ['Cosmetici Naturali', 'Prodotti Viso', 'Prodotti Corpo'],
  locations: 8,
  activeRewards: 12,
  activeUsers: 2450,
  totalRedemptions: 8920,
  status: 'active'
}, {
  id: 'PTR-002',
  name: 'NaturaSì',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2d/Naturas%C3%AC_Logo.svg/250px-Naturas%C3%AC_Logo.svg.png',
  website: 'https://www.naturasi.it',
  email: 'info@naturasi.it',
  description: 'Catena di supermercati biologici con prodotti alimentari e per la casa certificati.',
  categories: ['Alimentazione Bio', 'Prodotti Casa', 'Cosmetici Naturali'],
  locations: 15,
  activeRewards: 8,
  activeUsers: 5230,
  totalRedemptions: 15670,
  status: 'active'
}, {
  id: 'PTR-003',
  name: 'BioSapori',
  logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcThSa_iTnoCIROu27zvH2FkOYz61uIPKSRfxQ&s',
  website: 'https://www.biosapori.com',
  email: 'contatti@biosapori.it',
  description: 'Prodotti biologici locali e workshop di sostenibilità.',
  categories: ['Alimentazione Bio', 'Workshop', 'Prodotti Locali'],
  locations: 3,
  activeRewards: 5,
  activeUsers: 890,
  totalRedemptions: 2340,
  status: 'active'
}, {
  id: 'PTR-004',
  name: 'EcoCasa',
  logo: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=200&h=80&fit=crop',
  website: 'https://www.ecocasa.it',
  email: 'info@ecocasa.it',
  description: 'Prodotti per la casa ecologici e sostenibili, detergenti naturali e accessori zero waste.',
  categories: ['Prodotti Casa', 'Detergenza Eco', 'Zero Waste'],
  locations: 6,
  activeRewards: 7,
  activeUsers: 1850,
  totalRedemptions: 4560,
  status: 'active'
}, {
  id: 'PTR-005',
  name: 'GreenMarket',
  logo: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=200&h=80&fit=crop',
  website: 'https://www.greenmarket.it',
  email: 'contatti@greenmarket.it',
  description: 'Mercato biologico online con consegna a domicilio di prodotti freschi e confezionati.',
  categories: ['Alimentazione Bio', 'Delivery', 'Prodotti Freschi'],
  locations: 12,
  activeRewards: 10,
  activeUsers: 3420,
  totalRedemptions: 9870,
  status: 'active'
}];
const initialRewards: Reward[] = [{
  id: 'RWD-001',
  partnerId: 'PTR-001',
  code: 'SIERO-HA-001',
  name: 'Siero Acido Ialuronico Multi-Peso',
  partner: 'La Saponaria',
  type: 'Prodotto Viso',
  status: 'Attivo',
  cost: 50,
  image: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=200&h=200&fit=crop',
  availability: {
    current: 45,
    total: 100
  },
  redemptionsPeriod: 28,
  redemptionsTotal: 234,
  lastRedemption: '2 ore fa',
  location: 'Milano Centro',
  startDate: '2026-03-01',
  endDate: '2026-12-31'
}, {
  id: 'RWD-002',
  partnerId: 'PTR-001',
  code: 'SOLIDI-KIT-002',
  name: 'Kit Cosmetici Solidi Zero Waste',
  partner: 'La Saponaria',
  type: 'Cosmetici Solidi',
  status: 'Attivo',
  cost: 50,
  image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=200&h=200&fit=crop',
  availability: {
    current: 12,
    total: 50
  },
  redemptionsPeriod: 22,
  redemptionsTotal: 187,
  lastRedemption: '5 ore fa',
  location: 'Roma Cola di Rienzo',
  startDate: '2026-03-15',
  endDate: '2026-08-30'
}, {
  id: 'RWD-003',
  partnerId: 'PTR-002',
  code: 'VOUCHER-20',
  name: 'Buono Sconto 20€ su Acquisto',
  partner: 'NaturaSì',
  type: 'Buono Sconto',
  status: 'Attivo',
  cost: 40,
  image: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=200&h=200&fit=crop',
  availability: {
    current: 0,
    total: 0,
    unlimited: true
  },
  redemptionsPeriod: 45,
  redemptionsTotal: 312,
  lastRedemption: '1 ora fa',
  location: 'Tutte le Sedi',
  startDate: null,
  endDate: null
}];
const initialClaims: Claim[] = [{
  id: 'RC-2026-001',
  date: '2026-03-15 14:32',
  reward: 'Siero Acido Ialuronico Multi-Peso',
  partner: 'La Saponaria',
  partnerId: 'PTR-001',
  diamonds: 50,
  userId: 'USR-001',
  status: 'Utilizzato',
  method: 'Ritiro in Negozio',
  location: 'Milano Centro',
  code: '-',
  notes: 'Prodotto ritirato presso il negozio.'
}, {
  id: 'RC-2026-002',
  date: '2026-03-16 10:15',
  reward: 'Kit Cosmetici Solidi Zero Waste',
  partner: 'La Saponaria',
  partnerId: 'PTR-001',
  diamonds: 50,
  userId: 'USR-002',
  status: 'Riscattato',
  method: 'Ritiro in Negozio',
  location: 'Roma Cola di Rienzo',
  code: '-',
  notes: "In attesa di ritiro da parte dell'utente."
}, {
  id: 'RC-2026-003',
  date: '2026-04-16 11:45',
  reward: 'Buono Sconto 20€ su Acquisto',
  partner: 'NaturaSì',
  partnerId: 'PTR-002',
  diamonds: 40,
  userId: 'USR-003',
  status: 'Riscattato',
  method: 'Codice',
  location: 'Bologna',
  code: 'SCONTO20-XYZ123',
  notes: 'Codice generato e inviato via email.'
}];
// ============================================================================
// PLATFORM ANALYTICS MOCK DATA
// ============================================================================
const generateAnalyticsData = (): PlatformAnalytics => {
  // Generate 90 days of data
  const usersSeries: TimeSeriesData[] = [];
  const revenueSeries: TimeSeriesData[] = [];
  const today = new Date();
  for (let i = 89; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    // User growth with trend
    const baseUsers = 2500 + (89 - i) * 15;
    const variance = Math.sin(i / 7) * 100;
    usersSeries.push({
      date: dateStr,
      value: Math.round(baseUsers + variance)
    });
    // Revenue with trend
    const baseRevenue = 15000 + (89 - i) * 200;
    const revenueVariance = Math.sin(i / 5) * 2000;
    revenueSeries.push({
      date: dateStr,
      value: Math.round(baseRevenue + revenueVariance)
    });
  }
  return {
    kpis: {
      totalUsers: 4235,
      engagementRate: 68.5,
      revenue: 89420,
      retention: 82.3
    },
    usersSeries,
    revenueSeries,
    segments: [{
      id: 'mobile',
      name: 'Mobile',
      users: 2847,
      engagement: 72.4,
      revenue: 45230,
      trend: 12.5
    }, {
      id: 'web',
      name: 'Web',
      users: 1388,
      engagement: 61.2,
      revenue: 28190,
      trend: 8.3
    }, {
      id: 'referral',
      name: 'Referral',
      users: 892,
      engagement: 78.9,
      revenue: 12450,
      trend: 18.7
    }, {
      id: 'partner',
      name: 'Partner',
      users: 654,
      engagement: 85.3,
      revenue: 18920,
      trend: 22.1
    }, {
      id: 'organic',
      name: 'Organic',
      users: 1234,
      engagement: 58.6,
      revenue: 15630,
      trend: 5.2
    }]
  };
};
const initialPlatformAnalytics = generateAnalyticsData();
// ============================================================================
// IMPACT DATA MOCK DATA
// ============================================================================
const generateImpactData = (): ImpactData => {
  // Generate 90 days of impact data
  const impactSeries: ImpactTimeSeriesData[] = [];
  const today = new Date();
  for (let i = 89; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    // CO2 saved with growth trend
    const baseCO2 = 1200 + (89 - i) * 8;
    const co2Variance = Math.sin(i / 7) * 50;
    // Plastic avoided with growth trend
    const basePlastic = 450 + (89 - i) * 3;
    const plasticVariance = Math.sin(i / 5) * 20;
    // Trees planted with growth trend
    const baseTrees = 80 + (89 - i) * 0.5;
    const treesVariance = Math.sin(i / 6) * 5;
    impactSeries.push({
      date: dateStr,
      co2: Math.round(baseCO2 + co2Variance),
      plastic: Math.round(basePlastic + plasticVariance),
      trees: Math.round(baseTrees + treesVariance)
    });
  }
  return {
    kpis: {
      co2Saved: 12450,
      plasticAvoided: 4230,
      treesPlanted: 156
    },
    impactSeries,
    categoryData: [{
      category: 'Alimentazione',
      value: 3850
    }, {
      category: 'Mobilità',
      value: 4200
    }, {
      category: 'Casa',
      value: 2100
    }, {
      category: 'Shopping',
      value: 2300
    }],
    topActions: [{
      id: 'ACT-001',
      name: 'Utilizzo Borraccia Riutilizzabile',
      category: 'Shopping',
      completions: 2847,
      co2: 1420,
      lastUpdated: '2 ore fa',
      description: "Riduzione dell'uso di bottiglie di plastica monouso attraverso l'utilizzo quotidiano di borracce riutilizzabili. Ogni utilizzo evita circa 0.5kg di CO₂ e 50g di plastica."
    }, {
      id: 'ACT-002',
      name: 'Mobilità Sostenibile (Bici/Mezzi)',
      category: 'Mobilità',
      completions: 1923,
      co2: 3840,
      lastUpdated: '5 ore fa',
      description: "Scelta di mezzi di trasporto sostenibili come bicicletta o trasporto pubblico al posto dell'auto privata. Ogni viaggio evita in media 2kg di CO₂."
    }, {
      id: 'ACT-003',
      name: 'Spesa con Prodotti Locali Bio',
      category: 'Alimentazione',
      completions: 1654,
      co2: 2480,
      lastUpdated: '1 giorno fa',
      description: "Acquisto di prodotti alimentari biologici e a km zero, riducendo l'impatto del trasporto e supportando l'agricoltura sostenibile locale."
    }, {
      id: 'ACT-004',
      name: 'Raccolta Differenziata Corretta',
      category: 'Casa',
      completions: 1432,
      co2: 860,
      lastUpdated: '3 ore fa',
      description: "Separazione accurata dei rifiuti per favorire il riciclo e ridurre l'impatto ambientale. Include plastica, carta, vetro e organico."
    }, {
      id: 'ACT-005',
      name: 'Riduzione Sprechi Alimentari',
      category: 'Alimentazione',
      completions: 1289,
      co2: 1930,
      lastUpdated: '6 ore fa',
      description: 'Pianificazione della spesa e conservazione corretta degli alimenti per ridurre gli sprechi. Ogni kg di cibo non sprecato evita 2.5kg di CO₂.'
    }, {
      id: 'ACT-006',
      name: 'Uso Detergenti Ecologici',
      category: 'Casa',
      completions: 1156,
      co2: 580,
      lastUpdated: '1 giorno fa',
      description: 'Sostituzione di detergenti chimici con alternative ecologiche e biodegradabili per la pulizia della casa.'
    }, {
      id: 'ACT-007',
      name: 'Shopping con Borse Riutilizzabili',
      category: 'Shopping',
      completions: 987,
      co2: 490,
      lastUpdated: '4 ore fa',
      description: 'Utilizzo di borse riutilizzabili per la spesa invece di sacchetti di plastica monouso.'
    }, {
      id: 'ACT-008',
      name: 'Piantumazione Alberi',
      category: 'Ambiente',
      completions: 234,
      co2: 2340,
      lastUpdated: '2 giorni fa',
      description: "Partecipazione ad attività di piantumazione di alberi per compensare le emissioni di CO₂ e migliorare la qualità dell'aria."
    }]
  };
};
const initialImpactData = generateImpactData();
// ============================================================================
// CONTEXT
// ============================================================================
interface AdminDataContextType {
  // Collections
  users: User[];
  partners: Partner[];
  rewards: Reward[];
  claims: Claim[];
  platformAnalytics: PlatformAnalytics;
  impactData: ImpactData;
  // User CRUD
  createUser: (user: Omit<User, 'id'>) => User;
  updateUser: (id: string, updates: Partial<User>) => void;
  deleteUser: (id: string) => void;
  // Partner CRUD
  createPartner: (partner: Omit<Partner, 'id'>) => Partner;
  updatePartner: (id: string, updates: Partial<Partner>) => void;
  deletePartner: (id: string) => void;
  // Reward CRUD
  createReward: (reward: Omit<Reward, 'id'>) => Reward;
  updateReward: (id: string, updates: Partial<Reward>) => void;
  deleteReward: (id: string) => void;
  // Claim methods
  updateClaimStatus: (id: string, status: ClaimStatus) => void;
  // Toast
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}
const AdminDataContext = createContext<AdminDataContextType | undefined>(undefined);
// ============================================================================
// PROVIDER
// ============================================================================
interface AdminDataProviderProps {
  children: React.ReactNode;
}
export const AdminDataProvider: React.FC<AdminDataProviderProps> = ({
  children
}) => {
  const [users, setUsers] = useState<User[]>(initialUsers);
  const [partners, setPartners] = useState<Partner[]>(initialPartners);
  const [rewards, setRewards] = useState<Reward[]>(initialRewards);
  const [claims, setClaims] = useState<Claim[]>(initialClaims);
  const [platformAnalytics] = useState<PlatformAnalytics>(initialPlatformAnalytics);
  const [impactData] = useState<ImpactData>(initialImpactData);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });
  // Toast function
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({
      message,
      type,
      show: true
    });
    setTimeout(() => {
      setToast(prev => ({
        ...prev,
        show: false
      }));
    }, 3000);
  };
  // ============================================================================
  // USER CRUD
  // ============================================================================
  const createUser = (userData: Omit<User, 'id'>): User => {
    const newUser: User = {
      ...userData,
      id: crypto.randomUUID()
    };
    setUsers(prev => [...prev, newUser]);
    showToast('Utente creato con successo!');
    return newUser;
  };
  const updateUser = (id: string, updates: Partial<User>) => {
    setUsers(prev => prev.map(user => user.id === id ? {
      ...user,
      ...updates
    } : user));
    showToast('Utente aggiornato con successo!');
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(user => user.id !== id));
    showToast('Utente eliminato con successo!');
  };
  // ============================================================================
  // PARTNER CRUD
  // ============================================================================
  const createPartner = (partnerData: Omit<Partner, 'id'>): Partner => {
    const newPartner: Partner = {
      ...partnerData,
      id: crypto.randomUUID()
    };
    setPartners(prev => [...prev, newPartner]);
    showToast('Partner creato con successo!');
    return newPartner;
  };
  const updatePartner = (id: string, updates: Partial<Partner>) => {
    setPartners(prev => prev.map(partner => partner.id === id ? {
      ...partner,
      ...updates
    } : partner));
    showToast('Partner aggiornato con successo!');
  };
  const deletePartner = (id: string) => {
    setPartners(prev => prev.filter(partner => partner.id !== id));
    showToast('Partner eliminato con successo!');
  };
  // ============================================================================
  // REWARD CRUD
  // ============================================================================
  const createReward = (rewardData: Omit<Reward, 'id'>): Reward => {
    const newReward: Reward = {
      ...rewardData,
      id: crypto.randomUUID()
    };
    setRewards(prev => [...prev, newReward]);
    showToast('Premio creato con successo!');
    return newReward;
  };
  const updateReward = (id: string, updates: Partial<Reward>) => {
    setRewards(prev => prev.map(reward => reward.id === id ? {
      ...reward,
      ...updates
    } : reward));
    showToast('Premio aggiornato con successo!');
  };
  const deleteReward = (id: string) => {
    setRewards(prev => prev.filter(reward => reward.id !== id));
    showToast('Premio eliminato con successo!');
  };
  // ============================================================================
  // CLAIM METHODS
  // ============================================================================
  const updateClaimStatus = (id: string, status: ClaimStatus) => {
    setClaims(prev => prev.map(claim => claim.id === id ? {
      ...claim,
      status
    } : claim));
    const statusMessages = {
      Riscattato: 'Riscatto confermato!',
      Utilizzato: 'Riscatto marcato come utilizzato!',
      Annullato: 'Riscatto annullato!'
    };
    showToast(statusMessages[status] || 'Stato aggiornato!');
  };
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value: AdminDataContextType = {
    users,
    partners,
    rewards,
    claims,
    platformAnalytics,
    impactData,
    createUser,
    updateUser,
    deleteUser,
    createPartner,
    updatePartner,
    deletePartner,
    createReward,
    updateReward,
    deleteReward,
    updateClaimStatus,
    showToast
  };
  return <AdminDataContext.Provider value={value} data-id="element-1419">
      {children}

      {/* Toast Notification */}
      {toast.show && <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in ${toast.type === 'success' ? 'bg-green-600 text-white' : toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`} data-id="element-1420">
          {toast.message}
        </div>}
    </AdminDataContext.Provider>;
};
// ============================================================================
// HOOK
// ============================================================================
export const useAdminData = (): AdminDataContextType => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within AdminDataProvider');
  }
  return context;
};